import { useMemo, useState } from 'react'
import { evaluate, CRITERIA } from '../../lib/bodyPerfection.js'
import ledger from '../../lib/bodyEnumerationLedger.json'
import styles from './BodyPerfection.module.css'

/* ⭐ HOW MANY GOVERNING BODIES ARE PERFECT? (Brian, 2026-10-19)
   Computed HERE, from the records this page already loaded, so the number moves
   the moment a date is promoted, a crown lands or a rep is assigned. Criterion 1
   (the universe enumerated from the body's OWN list) comes from the enumeration
   ledger, sr-registry/body-enumeration-ledger.json, bundled at build time: a body
   that is not in it is UNMEASURED, never perfect. The same evaluator file runs in
   jarvis-watch, so the board and the hourly watch cannot disagree about the rule.
   Read-only: this panel never writes. */

const nf = (n) => Number(n || 0).toLocaleString('en-US')
const TONE = { PERFECT: 'ok', FAILING: 'bad', UNMEASURED: 'muted' }
const PAGE = 25

function reasons(b) {
  if (b.status === 'PERFECT') return 'All seven criteria hold.'
  const parts = Object.entries(b.detail).map(([why, n]) => `${nf(n)} ${why}`)
  if (b.status === 'UNMEASURED') parts.unshift('Universe not yet enumerated from the body\'s own published list')
  return parts.join(' · ')
}

export default function BodyPerfection({ data, status, complete, onPickBody = null }) {
  const [tab, setTab] = useState('closest')
  const [shown, setShown] = useState(PAGE)
  const ready = status === 'done' && complete
  const res = useMemo(() => (ready ? evaluate((data || []).map((c) => c.raw || c), ledger) : null), [ready, data])

  if (!res) {
    return (
      <section className={`card ${styles.section}`} aria-label="Perfect governing bodies">
        <p className={styles.head}>Perfect governing bodies</p>
        <p className={styles.note}>
          {status === 'error' ? 'Not measured: the championship load failed, so no count is shown. A failed read is not a zero.'
            : 'Measuring… waits for the whole object to load. A partial load is not a denominator, so no number is shown until it finishes.'}
        </p>
      </section>
    )
  }

  const s = res.summary
  const lists = {
    closest: res.bodies.filter((b) => b.status !== 'PERFECT'),
    perfect: res.bodies.filter((b) => b.status === 'PERFECT'),
    failing: res.bodies.filter((b) => b.status === 'FAILING'),
    unmeasured: res.bodies.filter((b) => b.status === 'UNMEASURED'),
  }
  const rows = lists[tab]
  const top = Object.entries(s.failingByTopCriterion).sort((a, b) => b[1] - a[1])
    .map(([c, n]) => `${CRITERIA[c] || c} ${nf(n)}`).join(' · ')

  return (
    <section className={`card ${styles.section}`} aria-label="Perfect governing bodies">
      <p className={styles.head}>Perfect governing bodies: {nf(s.perfect)} of {nf(s.totalBodies)}</p>
      <p className={styles.big}>
        {nf(s.totalBodies)} bodies = {nf(s.perfect)} perfect + {nf(s.failing)} failing + {nf(s.unmeasured)} unmeasured
        {s.reconciles ? '' : ' (DOES NOT RECONCILE, so treat this as a defect)'}.
      </p>
      <p className={styles.note}>
        Measured on {nf(s.records)} records ({nf(s.recordsWithNoBody)} carry no governing body and belong to none).
        Failing, by top criterion: {top || 'none'}.
      </p>
      <p className={styles.note}>
        Perfect means all seven: enumerated from the body&apos;s own list · nothing missing · no phantoms or wrong labels ·
        every concluded title crowned (school plus athlete for individual titles) · every date real-sourced with its year ·
        body, state and rep on every record · no key collisions.
      </p>
      <div className={styles.tabs} role="tablist">
        {[['closest', 'Closest to perfect'], ['perfect', `Perfect (${nf(s.perfect)})`], ['failing', `Failing (${nf(s.failing)})`], ['unmeasured', `Unmeasured (${nf(s.unmeasured)})`]].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={styles.tab} data-on={tab === k ? '1' : '0'}
            onClick={() => { setTab(k); setShown(PAGE) }}>{label}</button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className={styles.note}>{tab === 'perfect' ? 'No body passes all seven criteria today.' : 'None.'}</p>
      ) : (
        <ul className={styles.list}>
          {rows.slice(0, shown).map((b) => (
            <li key={b.id} className={styles.row} data-tone={TONE[b.status]}>
              <div className={styles.rowTop}>
                <span className={styles.name}>{b.name}</span>
                <span className={styles.badge}>{b.status}{b.official != null ? ` · holds ${nf(b.held)} of ${nf(b.official)}` : ` · holds ${nf(b.held)}`}</span>
              </div>
              <p className={styles.why}>{reasons(b)}</p>
              {onPickBody ? (
                <button type="button" className={styles.more} onClick={() => onPickBody(b.gbValues[0] || b.name)}>
                  Show this body&rsquo;s card
                </button>
              ) : (
                <p className={styles.why} aria-disabled="true">Readout only: this body has no card on this page.</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {rows.length > shown && (
        <button type="button" className={styles.more} onClick={() => setShown((n) => n + PAGE * 4)}>
          Show more ({nf(rows.length - shown)} not shown of {nf(rows.length)})
        </button>
      )}
    </section>
  )
}
